import { nextTick, onBeforeUnmount, ref, type Ref, watch } from 'vue';
import type { VtkViewApi } from '@/src/types/vtk-types';
import { resetRendererForSurfaceView } from '@/src/utils/volumeProperties';

function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Defers mounting the heavy vtkVolume actor until after a boot overlay paints,
 * and keeps the overlay up through the first VR frame.
 */
export function useDeferredVolumeRenderingMount(
  volumeRenderingEnabled: Ref<boolean>,
  vtkView: Ref<VtkViewApi | undefined>
) {
  const volumeRepMounted = ref(false);
  const volumeRenderingBooting = ref(false);
  let bootGeneration = 0;

  watch(
    volumeRenderingEnabled,
    async (enabled) => {
      const generation = ++bootGeneration;

      if (!enabled) {
        volumeRepMounted.value = false;
        volumeRenderingBooting.value = false;

        const renderer = vtkView.value?.renderer;
        if (renderer) resetRendererForSurfaceView(renderer);
        vtkView.value?.requestRender({ immediate: true });
        return;
      }

      volumeRenderingBooting.value = true;
      volumeRepMounted.value = false;

      await nextTick();
      await nextPaint();
      if (generation !== bootGeneration) return;

      volumeRepMounted.value = true;

      await nextTick();
      await nextPaint();
      if (generation !== bootGeneration) return;

      vtkView.value?.requestRender({ immediate: true });

      await nextPaint();
      if (generation !== bootGeneration) return;

      volumeRenderingBooting.value = false;
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    bootGeneration += 1;
  });

  return {
    volumeRepMounted,
    volumeRenderingBooting,
  };
}
